// W41.0 E5 — the four mascots, as data.
//
// Spec §1 E5: hand-drawn SVG blobs, **closed** Catmull-Rom converted to cubic
// Bézier, 10–12 control points, petal counts 4/5/6/8, amplitude R/r in
// 1.11–1.19. The amplitude bound is the whole point: R1 used 1.43 and the result
// was a five-pointed star, not a cloud. `spec.amplitude` records the value each
// shipped path was generated at, so a future regeneration can reproduce the
// shape instead of nudging numbers until it looks about right.
//
// Each mascot is 1 body path + eyes + mouth + blush. The bodies come verbatim
// from `mockup/w41.0-empty.html`, which is what the acceptance images show —
// the same traceability argument as `icons.tsx`: the approved pixels are the
// specification.
//
// Placement (DESIGN.md §18.0):
//   6 瓣 coral  → 首頁「今日小問題」lime 卡
//   4 瓣 aqua   → 紀念日「下一個」lime 段
//   5 瓣 lilac  → 里程碑 / empty state
//   5 瓣 lime   → 互動 / 提示
//
// **A character never shares its card's colour.** That is why the empty state
// draws the lilac mascot in white on a lilac block (spec §1 E5).
//
// Data, not component, so `Mascot.tsx` can export only a component and satisfy
// `react-refresh/only-export-components`.

export type MascotName = "coral" | "aqua" | "lilac" | "lime";

export interface MascotSpec {
  /** Petal count of the underlying blob. */
  petals: number;
  /** R/r amplitude the shipped path was generated at (spec bound: 1.11–1.19). */
  amplitude: number;
  /** Body fill. Callers override it to draw the character white on a lilac block. */
  fill: string;
  /** The face, so it scales with the mascot rather than being pinned. */
  face: "dot-eyes" | "closed-eyes";
}

export interface MascotRecord {
  /** Body path, viewBox `0 0 200 200`. */
  d: string;
  spec: MascotSpec;
  /** Accessible name — the mascot is a labelled image, never decorative. */
  label: string;
}

export const MASCOTS: Record<MascotName, MascotRecord> = {
  coral: {
    // 6 petals · 慶祝
    d: "M171.5 119.0C168.8 129.3 154.0 136.4 145.3 145.2C136.6 153.9 129.6 168.7 119.3 171.4C109.0 174.2 95.5 165.0 83.5 161.8C71.6 158.7 55.3 159.9 47.8 152.4C40.2 144.9 41.4 128.6 38.2 116.7C35.0 104.8 25.7 91.3 28.5 81.0C31.2 70.7 46.0 63.6 54.7 54.8C63.4 46.1 70.4 31.3 80.7 28.6C91.0 25.8 104.5 35.0 116.5 38.2C128.4 41.3 144.7 40.1 152.2 47.6C159.8 55.1 158.6 71.4 161.8 83.3C165.0 95.2 174.3 108.7 171.5 119.0Z",
    spec: { petals: 6, amplitude: 1.17, fill: "#F34A42", face: "dot-eyes" },
    label: "珊瑚 · 慶祝",
  },
  aqua: {
    // 4 petals · 平靜
    d: "M171.8 130.4C165.4 145.6 143.4 158.0 126.3 164.9C109.3 171.8 84.8 178.3 69.6 171.8C54.4 165.4 42.0 143.4 35.1 126.3C28.2 109.3 21.7 84.8 28.2 69.6C34.6 54.4 56.6 42.0 73.7 35.1C90.7 28.2 115.2 21.7 130.4 28.2C145.6 34.6 158.0 56.6 164.9 73.7C171.8 90.7 178.3 115.2 171.8 130.4Z",
    spec: { petals: 4, amplitude: 1.11, fill: "#4AC2C6", face: "closed-eyes" },
    label: "水色 · 平靜",
  },
  lilac: {
    // 5 petals · 里程碑
    d: "M176.0 100.0C176.0 112.9 162.1 126.7 153.4 138.8C144.6 150.8 135.8 168.3 123.5 172.3C111.2 176.3 93.8 167.4 79.6 162.8C65.4 158.2 46.1 155.1 38.5 144.7C30.9 134.2 34.0 114.9 34.0 100.0C34.0 85.1 30.9 65.8 38.5 55.3C46.1 44.9 65.4 41.8 79.6 37.2C93.8 32.6 111.2 23.7 123.5 27.7C135.8 31.7 144.6 49.2 153.4 61.2C162.1 73.3 176.0 87.1 176.0 100.0Z",
    spec: { petals: 5, amplitude: 1.19, fill: "#A88BFF", face: "dot-eyes" },
    label: "丁香 · 里程碑",
  },
  lime: {
    // 5 petals · 互動
    d: "M165.1 137.3C159.0 148.0 139.2 151.4 125.8 157.5C112.4 163.5 96.8 175.9 84.7 173.4C72.6 170.9 63.2 153.2 53.3 142.3C43.5 131.4 26.8 120.4 25.4 108.1C24.1 95.8 38.0 81.4 45.3 68.7C52.6 55.9 58.0 36.7 69.2 31.6C80.5 26.5 98.5 35.3 112.9 38.3C127.3 41.3 147.3 40.5 155.6 49.6C163.8 58.8 161.0 78.6 162.6 93.2C164.2 107.8 171.2 126.6 165.1 137.3Z",
    spec: { petals: 5, amplitude: 1.15, fill: "#CDFB64", face: "dot-eyes" },
    label: "青檸 · 互動",
  },
};

export const MASCOT_NAMES = Object.keys(MASCOTS) as MascotName[];

/** Runtime access to the generation parameters, for the guard test. */
export const mascotSpec = (name: MascotName): MascotSpec => MASCOTS[name].spec;
